<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Geofence } from '@gpx-spa/geo-domain';
import ButtonPrime from 'primevue/button';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import {
  SHIPMENT_STOP_ROLE_VISUAL,
  shipmentStopRoleFromIndex,
} from '@gpx-spa/constants';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { ShipmentFormStopFieldErrors } from '@/components/shipment/form/utils/shipment-form-stop-validation';
import StopLocationField from '@/components/shipment/form/stops/StopLocationField.vue';
import StopRadiusField from '@/components/shipment/form/stops/StopRadiusField.vue';
import StopPlannedTimesFields from '@/components/shipment/form/stops/StopPlannedTimesFields.vue';

type StopLocationFieldExpose = {
  focusAddressInput?: () => boolean;
};

type PlannedTimeField = 'plannedDeparture' | 'plannedArrival';

const props = withDefaults(
  defineProps<{
    stop: ShipmentFormStopRow;
    index: number;
    totalStops: number;
    geofences: Geofence[];
    geofencesLoading: boolean;
    placesDisabled: boolean;
    fieldErrors?: ShipmentFormStopFieldErrors;
    suppressPlannedTimeManualMark?: boolean;
    disabledFields?: Partial<
      Record<'location' | 'radius' | 'plannedDeparture' | 'plannedArrival', boolean>
    >;
    lockReason?: string;
    lockBadgeLabel?: string;
    hideActions?: boolean;
  }>(),
  { suppressPlannedTimeManualMark: false, hideActions: false },
);

const emit = defineEmits<{
  plannedTimeUserEdit: [field: PlannedTimeField, value: Date | null];
}>();

const store = useShipmentFormStore();

const cardRole = computed(() =>
  shipmentStopRoleFromIndex(props.index, props.totalStops),
);

const roleVisual = computed(() => SHIPMENT_STOP_ROLE_VISUAL[cardRole.value]);

const roleLabelUpper = computed(() => cardRole.value.toUpperCase());

const isLocked = computed(() => Boolean(props.lockReason));

const canDelete = computed(
  () => !props.hideActions && props.totalStops > 2 && !props.disabledFields?.location,
);

function isFieldDisabled(
  field: 'location' | 'radius' | 'plannedDeparture' | 'plannedArrival',
): boolean {
  return Boolean(props.disabledFields?.[field]);
}

const addressPlaceholder = computed(() => {
  if (cardRole.value === 'origin') return 'Search pickup address...';
  if (cardRole.value === 'destination') return 'Search delivery address...';
  return 'Search waypoint address...';
});

const locationFieldRef = ref<StopLocationFieldExpose | null>(null);

function focusFirstField(): boolean {
  if (isFieldDisabled('location')) return false;
  return locationFieldRef.value?.focusAddressInput?.() ?? false;
}

defineExpose({
  focusFirstField,
});
</script>

<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4">
    <template v-if="isLocked">
      <div class="mb-3 flex items-center justify-between gap-2">
        <div class="flex min-w-0 items-center gap-3">
          <div
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
            :class="roleVisual.cardNumberCircleClass"
          >
            {{ index + 1 }}
          </div>
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gpx_gray-200"
          >
            {{ roleLabelUpper }}
          </span>
        </div>
        <Tag
          v-if="lockBadgeLabel"
          :value="lockBadgeLabel"
          severity="danger"
          icon="pi pi-lock"
          class="!shrink-0 !text-xs"
        />
      </div>

      <Message
        severity="error"
        size="small"
        :closable="false"
        class="mb-3 w-full"
      >
        {{ lockReason }}
      </Message>

      <div class="space-y-3">
        <StopLocationField
          ref="locationFieldRef"
          :stop="stop"
          :geofences="geofences"
          :geofences-loading="geofencesLoading"
          :places-disabled="placesDisabled"
          :field-errors="fieldErrors"
          :address-placeholder="addressPlaceholder"
          :location-disabled="isFieldDisabled('location')"
        >
          <template #companion>
            <StopRadiusField
              :stop="stop"
              :field-errors="fieldErrors"
              :radius-disabled="isFieldDisabled('radius')"
            />
          </template>
        </StopLocationField>

        <StopPlannedTimesFields
          :stop="stop"
          :card-role="cardRole"
          :field-errors="fieldErrors"
          :suppress-planned-time-manual-mark="suppressPlannedTimeManualMark"
          :departure-disabled="isFieldDisabled('plannedDeparture')"
          :arrival-disabled="isFieldDisabled('plannedArrival')"
          @planned-time-user-edit="(field, value) => emit('plannedTimeUserEdit', field, value)"
        />
      </div>
    </template>

    <template v-else>
      <div class="flex items-start gap-3">
        <div class="flex shrink-0 flex-col items-center pt-1">
          <div
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
            :class="roleVisual.cardNumberCircleClass"
          >
            {{ index + 1 }}
          </div>
        </div>

        <div class="min-w-0 flex-1 space-y-3">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
              :class="roleVisual.cardRoleBadgeClass"
            >
              {{ cardRole }}
            </span>
            <div class="flex flex-wrap items-center gap-2">
              <ButtonPrime
                v-if="canDelete"
                type="button"
                severity="danger"
                outlined
                size="small"
                label="Remove"
                tabindex="-1"
                @click="store.removeStopAt(index)"
              />
            </div>
          </div>

          <StopLocationField
            ref="locationFieldRef"
            :stop="stop"
            :geofences="geofences"
            :geofences-loading="geofencesLoading"
            :places-disabled="placesDisabled"
            :field-errors="fieldErrors"
            :address-placeholder="addressPlaceholder"
            :location-disabled="isFieldDisabled('location')"
          >
            <template #companion>
              <StopRadiusField
                :stop="stop"
                :field-errors="fieldErrors"
                :radius-disabled="isFieldDisabled('radius')"
              />
            </template>
          </StopLocationField>

          <StopPlannedTimesFields
            :stop="stop"
            :card-role="cardRole"
            :field-errors="fieldErrors"
            :suppress-planned-time-manual-mark="suppressPlannedTimeManualMark"
            :departure-disabled="isFieldDisabled('plannedDeparture')"
            :arrival-disabled="isFieldDisabled('plannedArrival')"
            @planned-time-user-edit="(field, value) => emit('plannedTimeUserEdit', field, value)"
          />
        </div>
      </div>
    </template>
  </div>
</template>
